import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowUpRight, Loader2, User, Building2, Phone, Mail, Lock } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { registerAccount, loginAccount } from '../api';
import { findCategory } from '../mock';
import { useToast } from '../hooks/use-toast';

function apiError(detail, fallback) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(' ');
  return fallback;
}

export default function AccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const catParam = new URLSearchParams(location.search).get('categorie');
  const cat = findCategory(catParam);

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', password: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const destination = useMemo(() => {
    return catParam ? `/produse?categorie=${encodeURIComponent(catParam)}` : '/produse';
  }, [catParam]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'register') {
        await registerAccount({
          name: form.name.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          password: form.password,
        });
        toast({ title: 'Cont creat', description: 'Ești autentificat.' });
      } else {
        await loginAccount(form.email.trim(), form.password);
        toast({ title: 'Autentificare reușită' });
      }
      navigate(destination, { replace: true });
    } catch (err) {
      const msg = apiError(err?.response?.data?.detail, 'A apărut o eroare. Încearcă din nou.');
      toast({ title: 'Eroare', description: msg });
    }
    setBusy(false);
  };

  const inputCls = 'w-full h-11 rounded-md bg-white border border-slate-300 text-slate-900 text-[15px] pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-[#f5a524] focus:border-transparent';

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="account-page">
      <Header />
      <main className="flex-1 pt-28 md:pt-32 pb-20">
        <div className="egea-container">
          <div className="max-w-[460px] mx-auto">
            <span className="section-eyebrow">Cont distribuitor</span>
            <h1 className="font-display font-black uppercase tracking-tight text-slate-900 text-3xl md:text-4xl leading-tight mt-3">
              {mode === 'register' ? 'Creează cont' : 'Autentificare'}
            </h1>
            <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
              {cat
                ? `Autentifică-te pentru a vedea gama ${cat.title} și a cere oferte tehnice.`
                : 'Autentifică-te pentru a accesa catalogul de produse.'}
            </p>

            {/* Mode switch */}
            <div className="mt-7 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg" data-testid="account-mode-switch">
              <button
                type="button"
                onClick={() => setMode('login')}
                data-testid="account-tab-login"
                className={`h-10 rounded-md text-[14px] font-bold uppercase tracking-wide transition-colors ${mode === 'login' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                Autentificare
              </button>
              <button
                type="button"
                onClick={() => setMode('register')}
                data-testid="account-tab-register"
                className={`h-10 rounded-md text-[14px] font-bold uppercase tracking-wide transition-colors ${mode === 'register' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                Cont nou
              </button>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="account-form">
              {mode === 'register' && (
                <>
                  <div className="relative">
                    <User size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={form.name} onChange={set('name')} required placeholder="Nume persoană" data-testid="account-name" className={inputCls} />
                  </div>
                  <div className="relative">
                    <Building2 size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={form.company} onChange={set('company')} placeholder="Nume firmă" data-testid="account-company" className={inputCls} />
                  </div>
                  <div className="relative">
                    <Phone size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={form.phone} onChange={set('phone')} placeholder="Telefon" data-testid="account-phone" className={inputCls} />
                  </div>
                </>
              )}
              <div className="relative">
                <Mail size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" value={form.email} onChange={set('email')} required placeholder="Email" data-testid="account-email" className={inputCls} />
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="password" value={form.password} onChange={set('password')} required placeholder={mode === 'register' ? 'Parolă (minim 3 caractere)' : 'Parolă'} data-testid="account-password" className={inputCls} />
              </div>

              <button type="submit" disabled={busy} data-testid="account-submit" className="btn-accent w-full h-12 text-[14px] uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={17} className="animate-spin" /> : <ArrowUpRight size={17} />}
                {mode === 'register' ? 'Creează cont' : 'Intră în cont'}
              </button>
            </form>

            <p className="mt-5 text-[13px] text-slate-500 text-center">
              {mode === 'register' ? 'Ai deja cont? ' : 'Nu ai cont încă? '}
              <button
                type="button"
                onClick={() => setMode(mode === 'register' ? 'login' : 'register')}
                data-testid="account-switch-link"
                className="text-[#d97706] font-semibold hover:underline"
              >
                {mode === 'register' ? 'Autentifică-te' : 'Creează unul acum'}
              </button>
            </p>

            <div className="mt-8 text-center">
              <Link to="/" className="text-[13px] text-slate-400 hover:text-slate-700 transition-colors">← Înapoi la pagina principală</Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
